import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { useTranslation } from 'react-i18next';
import api from '../api';

function AdminLoans() {
  const [loans, setLoans] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [creating, setCreating] = useState(false);
  const [generatingContract, setGeneratingContract] = useState(null);
  const [deletingLoan, setDeletingLoan] = useState(null);
  const [formData, setFormData] = useState({
    lender_id: '',
    capital: '',
    interest_rate: 6.0,
    duration_months: 12,
    start_date: new Date().toISOString().split('T')[0]
  });
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [loansRes, lendersRes] = await Promise.all([
        api.get('/api/loans'),
        api.get('/api/admin/lenders')
      ]);
      setLoans(loansRes.data);
      setLenders(lendersRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      if (showEditModal && editingLoan) {
        const updateData = {
          capital: parseFloat(formData.capital),
          interest_rate: parseFloat(formData.interest_rate),
          duration_months: parseInt(formData.duration_months),
          start_date: new Date(formData.start_date).toISOString()
        };

        await api.put(`/api/admin/loans/${editingLoan._id}`, updateData);
        toast.success(t('messages.loanUpdated'));
        closeModal();
      } else {
        const startDate = new Date(formData.start_date);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + parseInt(formData.duration_months));

        await api.post('/api/admin/loans', {
          lender_id: formData.lender_id,
          capital: parseFloat(formData.capital),
          interest_rate: parseFloat(formData.interest_rate),
          interest_base: 'annual',
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          duration_months: parseInt(formData.duration_months),
          frequency: 'daily'
        });

        toast.success(t('messages.loanCreated'));
        closeModal();  // Use closeModal instead of just setShowCreateModal
      }
      
      fetchData();
    } catch (error) {
      toast.error(t('messages.error') + ': ' + (error.response?.data?.detail || t('messages.error')));
    } finally {
      setCreating(false);
    }
  };

  const openEditLoan = (loan) => {
    setEditingLoan(loan);
    // Parse date correctly to avoid timezone issues
    const startDate = new Date(loan.start_date);
    const localDateStr = startDate.toISOString().split('T')[0];
    
    setFormData({
      lender_id: loan.lender_id,
      capital: loan.capital.toString(),
      interest_rate: loan.interest_rate,
      duration_months: loan.duration_months,
      start_date: localDateStr
    });
    setShowEditModal(true);
  };

  const handleGenerateContract = async (loanId, language = 'es') => {
    setGeneratingContract(loanId);
    try {
      await api.post('/api/admin/contracts/generate', {
        loan_id: loanId,
        language: language
      });
      toast.success(t('messages.contractGenerated'));
      fetchData();
    } catch (error) {
      toast.error(t('common.error') + ': ' + (error.response?.data?.detail || t('errors.unknown')));
    } finally {
      setGeneratingContract(null);
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingLoan(null);
    setFormData({
      lender_id: '',
      capital: '',
      interest_rate: 6.0,
      duration_months: 12,
      start_date: new Date().toISOString().split('T')[0]
    });
  };
  
  const handleDeleteLoan = async (loanId, lenderName) => {
    const msg = i18n.language === 'ca' 
      ? `Eliminar préstec de ${lenderName}?\n\nS'eliminaran el contracte, firmes i tots els documents associats.\n\nAquesta acció no es pot desfer.`
      : `Eliminar préstamo de ${lenderName}?\n\nSe eliminarán el contrato, firmas y todos los documentos asociados.\n\nEsta acción no se puede deshacer.`;
    if (!confirm(msg)) {
      return;
    }
    
    setDeletingLoan(loanId);
    
    try {
      const response = await api.delete(`/api/admin/loans/${loanId}`);
      
      // Check if it was already deleted (idempotent response)
      if (response.data.already_deleted) {
        toast.info(t('misc.loanAlreadyDeleted'));
      } else {
        toast.info(t('misc.loanDeleted'));
      }
      
      fetchData(); // Refresh the list
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info(t('misc.loanAlreadyDeleted'));
        fetchData(); // Refresh to remove from UI
      } else {
        toast.error(t('common.error') + ': ' + (error.response?.data?.detail || t('errors.unknown')));
      }
    } finally {
      setDeletingLoan(null);
    }
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar 
          title={t('admin.loans')} 
          actions={
            <button
              onClick={() => { setFormData({ lender_id: '', capital: '', interest_rate: 6.0, duration_months: 12, start_date: new Date().toISOString().split('T')[0] }); setShowCreateModal(true); }}
              className="btn-primary"
              data-testid="create-loan-button"
              style={{ padding: '0.75rem 1.5rem' }}
            >
              + Crear Préstamo
            </button>
          }
        />

        <main style={{ padding: '2.5rem' }} className="fade-in">
          {/* Page Title with better spacing */}
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'rgba(255,255,255,0.95)', marginBottom: '0.5rem' }}>
              {t('admin.loans')}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem' }}>
              {t('admin.activeLoans')}: {loans.filter(l => l.status === 'active').length} / {loans.length}
            </p>
          </div>
          
          <div className="dark-table" style={{ marginTop: '1rem' }}>
            <table style={{ width: '100%' }} data-testid="admin-loans-table">
              <thead>
                <tr>
                  <th>{t('admin.lenderName')}</th>
                  <th>{t('loans.capital')}</th>
                  <th>{t('loans.interestRate')}</th>
                  <th>{t('loans.status')}</th>
                  <th>{t('loans.daysRemaining')}</th>
                  <th>{t('common.viewDetails')}</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan) => {
                  const lender = lenders.find(l => l.user._id === loan.lender_id);
                  return (
                    <tr key={loan._id}>
                      <td data-label="Prestamista" style={{ fontWeight: '600' }}>
                        {lender ? `${lender.user.name} ${lender.user.surname}` : loan.lender_id}
                      </td>
                      <td data-label="Capital" style={{ fontWeight: '700', color: '#103766' }}>€{loan.capital.toLocaleString('es-ES')}</td>
                      <td data-label="Tipus">{loan.interest_rate}%</td>
                      <td data-label="Estat">
                        <span className={`status-badge ${loan.status === 'active' ? 'active' : loan.status === 'cancelled' ? 'cancelled' : 'pending'}`} style={
                          loan.status === 'cancelled' ? { background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' } :
                          loan.status === 'matured' ? { background: 'rgba(107,114,128,0.15)', color: '#9ca3af', border: '1px solid rgba(107,114,128,0.3)' } : {}
                        }>
                          {loan.status === 'active' ? t('status.active') : 
                           loan.status === 'cancelled' ? t('status.cancelled') : 
                           loan.status === 'matured' ? t('status.matured') : t('status.pending')}
                        </span>
                      </td>
                      <td data-label="Dies restants">{loan.days_remaining || 0} {t('dashboard.days')}</td>
                      <td data-label="Accions">
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {loan.status === 'pending_signature' && (
                            <button
                              onClick={() => openEditLoan(loan)}
                              className="btn-secondary"
                              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                              data-testid={`edit-loan-${loan._id}`}
                            >
                              ✏️ Editar
                            </button>
                          )}
                          {!loan.contract_id ? (
                            <>
                              <button
                                onClick={() => navigate(`/admin/contracts/edit/${loan._id}`)}
                                className="btn-secondary"
                                style={{ 
                                  padding: '0.5rem 1rem', 
                                  fontSize: '0.875rem',
                                  background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                                  color: 'white',
                                  border: 'none'
                                }}
                                data-testid={`edit-contract-${loan._id}`}
                              >
                                📝 Personalizar Contrato
                              </button>
                              <button
                                onClick={() => handleGenerateContract(loan._id, 'es')}
                                disabled={generatingContract === loan._id}
                                className="btn-primary"
                                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                                data-testid={`generate-contract-es-${loan._id}`}
                              >
                                {generatingContract === loan._id ? '⏳' : '📄 Generar ES'}
                              </button>
                              <button
                                onClick={() => handleGenerateContract(loan._id, 'ca')}
                                disabled={generatingContract === loan._id}
                                className="btn-primary"
                                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                                data-testid={`generate-contract-ca-${loan._id}`}
                              >
                                📄 CA
                              </button>
                              {(
                                <button
                                  onClick={() => handleDeleteLoan(loan._id, lender ? `${lender.user.name} ${lender.user.surname}` : 'prestamista')}
                                  disabled={deletingLoan === loan._id}
                                  className="btn-ghost"
                                  data-testid={`delete-loan-${loan._id}`}
                                  style={{ 
                                    padding: '0.5rem 1rem', 
                                    fontSize: '0.875rem',
                                    color: deletingLoan === loan._id ? '#9ca3af' : '#ef4444',
                                    border: `1px solid ${deletingLoan === loan._id ? 'rgba(156, 163, 175, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                                    background: deletingLoan === loan._id ? 'rgba(156, 163, 175, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    cursor: deletingLoan === loan._id ? 'not-allowed' : 'pointer',
                                    opacity: deletingLoan === loan._id ? 0.5 : 1
                                  }}
                                >
                                  {deletingLoan === loan._id ? '⏳' : '🗑️'}
                                </button>
                              )}
                              <button
                                onClick={() => navigate(`/loans/${loan._id}`)}
                                className="btn-secondary"
                                style={{ 
                                  padding: '0.4rem 0.6rem', 
                                  fontSize: '0.8125rem',
                                  background: 'rgba(124,58,237,0.15)',
                                  color: '#a78bfa',
                                  border: '1px solid rgba(124,58,237,0.3)',
                                  cursor: 'pointer'
                                }}
                              >
                                📁
                              </button>
                            </>
                          ) : (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ color: '#10b981', fontWeight: '600', fontSize: '0.875rem' }}>✓ {t('status.contractGenerated')}</span>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const response = await api.get(`/api/contracts/${loan.contract_id}/download`, {
                                      responseType: 'blob'
                                    });
                                    
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', `contracte_${loan.contract_id}.pdf`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(url);
                                  } catch (error) {
                                    toast.error('Error en descarregar: ' + (error.response?.data?.detail || 'Error'));
                                  }
                                }}
                                className="btn-secondary"
                                style={{ 
                                  padding: '0.4rem 0.8rem', 
                                  fontSize: '0.8125rem',
                                  background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                                  color: 'white',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                                data-testid={`download-contract-${loan._id}`}
                              >
                                📥 {t('misc.download')}
                              </button>
                              <button
                                onClick={() => navigate(`/loans/${loan._id}`)}
                                className="btn-secondary"
                                style={{ 
                                  padding: '0.4rem 0.8rem', 
                                  fontSize: '0.8125rem',
                                  background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
                                  color: 'white',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                                data-testid={`docs-loan-${loan._id}`}
                              >
                                📁 {t('documents.title')}
                              </button>
                              {loan.status === 'active' && (!loan.cancellation_request || loan.cancellation_request.status === 'rejected') && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const msg = i18n.language === 'ca'
                                      ? `Cancel·lar préstec de ${loan.lender_name || '?'} per decisió de l'empresa?\n\nEs calcularà l'interès prorrateig SENSE penalització.\nCapital: €${loan.capital?.toLocaleString()}\nTipus: ${loan.interest_rate}%`
                                      : `Cancelar préstamo de ${loan.lender_name || '?'} por decisión de la empresa?\n\nSe calculará el interés prorrateado SIN penalización.\nCapital: €${loan.capital?.toLocaleString()}\nTipo: ${loan.interest_rate}%`;
                                    if (!confirm(msg)) return;
                                    try {
                                      const resp = await api.post(`/api/admin/loans/${loan._id}/company-cancellation`);
                                      toast.success(`${i18n.language === 'ca' ? 'Cancel·lat' : 'Cancelado'}: €${resp.data.final_interest?.toFixed(2)} ${i18n.language === 'ca' ? 'interessos' : 'intereses'} (${resp.data.days_active}d)`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Error');
                                    }
                                  }}
                                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                >
                                  {i18n.language === 'ca' ? 'Cancel·lar' : 'Cancelar'}
                                </button>
                              )}
                              {/* Void contract button - for any status with contract */}
                              {(loan.status === 'pending_signature' || loan.status === 'active') && (
                                <button
                                  data-testid={`void-contract-${loan._id}`}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const msg = i18n.language === 'ca'
                                      ? `ANUL·LAR el contracte de ${loan.lender_name || '?'}?\n\nAixò eliminarà el contracte i totes les firmes.\nEl préstec tornarà a estat "pendent de firma" per poder generar un nou contracte.\n\nEscriu "ANULAR" per confirmar:`
                                      : `ANULAR el contrato de ${loan.lender_name || '?'}?\n\nEsto eliminará el contrato y todas las firmas.\nEl préstamo volverá a estado "pendiente de firma" para poder generar un nuevo contrato.\n\nEscribe "ANULAR" para confirmar:`;
                                    const confirmText = prompt(msg);
                                    if (confirmText !== 'ANULAR') return;
                                    try {
                                      await api.post(`/api/admin/contracts/${loan.contract_id}/void`);
                                      toast.success(i18n.language === 'ca' ? 'Contracte anul·lat correctament' : 'Contrato anulado correctamente');
                                      fetchData();
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Error');
                                    }
                                  }}
                                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', background: 'rgba(251,146,60,0.1)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.3)', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                >
                                  {i18n.language === 'ca' ? 'Anul·lar contracte' : 'Anular contrato'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </main>
      </div>

      {/* Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal-content" style={{ maxWidth: '700px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#103766', margin: 0 }}>
                {showEditModal ? t('modal.editLoan') : t('modal.createLoan')}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280'
                }}
                data-testid="close-modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateLoan} style={{ display: 'grid', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>{t('labels.lender')} *</label>
                <select
                  required
                  value={formData.lender_id}
                  onChange={(e) => setFormData({...formData, lender_id: e.target.value})}
                  data-testid="loan-lender-select"
                  disabled={showEditModal}
                >
                  <option value="">{t('placeholders.selectLender')}</option>
                  {lenders.map((l) => (
                    <option key={l.user._id} value={l.user._id}>
                      {l.user.name} {l.user.surname} - {l.user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>{t('labels.capital')} *</label>
                  <input
                    type="number"
                    required
                    min="100"
                    step="0.01"
                    value={formData.capital}
                    onChange={(e) => setFormData({...formData, capital: e.target.value})}
                    placeholder={t('placeholders.amount')}
                    data-testid="loan-capital-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>{t('labels.interestRate')} *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({...formData, interest_rate: e.target.value})}
                    data-testid="loan-rate-input"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Duración (meses) *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="120"
                    value={formData.duration_months}
                    onChange={(e) => setFormData({...formData, duration_months: e.target.value})}
                    data-testid="loan-duration-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Fecha de Inicio *</label>
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    data-testid="loan-startdate-input"
                  />
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '2px solid #93c5fd'
              }}>
                <p style={{ fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>Resumen:</p>
                <p style={{ color: '#1e3a8a' }}>
                  Préstamo de <strong>€{formData.capital || '0'}</strong> al <strong>{formData.interest_rate}%</strong> durante <strong>{formData.duration_months} meses</strong>
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                  data-testid="cancel-create-loan"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-primary"
                  data-testid="submit-create-loan"
                >
                  {creating ? t('status.saving') : (showEditModal ? t('buttons.save') : t('buttons.createLoanButton'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLoans;